import React from 'react';
import {observer} from 'mobx-react';
import {toJS} from 'mobx';

import autobind from 'common/decorators/autobind.js';
import CollapsibleText from 'core/components/CollapsibleText.js';
import ExtraInfo from 'core/components/ExtraInfo.js';
import HicapsBadge from 'core/components/HicapsBadge.js';
import Icon from 'core/components/Icon.js';
import Link from 'core/components/Link.js';
import ImageGallery from 'core/components/ImageGallery.js';
import HealthFundPicker from 'core/components/HealthFundPicker.js';
import NoResultsNotice from 'directory/components/NoResultsNotice.js';
import PractitionerList from 'practices/components/PractitionerList.js';
import {PracticeStoreContext} from 'core/stores/RootStore.js';
import Video from 'professional/components/Video.js';

export default
@observer
class PracticeOverview extends React.Component {
    static contextType = PracticeStoreContext;

    constructor(props) {
        super(props);
        this.state = {
            selectedIndex: undefined,
        };
    }

    @autobind
    onClickImage(index) {
        this.context.showModal('gallery', {
            selectedIndex: index,
        });
    }

    renderPracticeFile(file) {
        if (this.context.isHospitalPage) {
            const {eventUrl, data} = this.context.fetchOpenFileData(file.id);
            return (
                <li key={file.id}>
                    <Link
                        action={() =>
                            this.context.rootStore.analyticsStore.genericTrackEvent(
                                eventUrl,
                                data,
                            )
                        }
                        href={file.url}
                        isExternalLink={true}
                    >
                        <Icon name="factsheets" />
                        {file.name}
                    </Link>
                </li>
            );
        }
        const {eventName, data} = this.context.fetchOpenFileData(file.id);

        return (
            <li key={file.id}>
                <Link
                    action={() =>
                        this.context.rootStore.analyticsStore.trackEvent(
                            eventName,
                            data,
                        )
                    }
                    href={file.url}
                    isExternalLink={true}
                >
                    <Icon name="factsheets" />
                    {file.name}
                </Link>
            </li>
        );
    }

    renderHicaps() {
        const {practice} = this.context;

        if (practice.offersHicaps) {
            return <HicapsBadge isPracticeLocation />;
        }

        return null;
    }

    renderPractitionersList() {
        const {
            rootStore: {
                paramStore: {isClient},
            },
            isHospitalPage,
        } = this.context;
        const practice = toJS(this.context.practice);

        if (isHospitalPage) {
            return practice.practitioners?.length ? (
                <PractitionerList />
            ) : (
                <NoResultsNotice>
                    <p>No affiliated specialists found at {practice.name}</p>
                </NoResultsNotice>
            );
        } else if (!isClient && practice.practitioners?.length > 0) {
            return <PractitionerList />;
        }
    }

    @autobind
    async HFselector(e) {
        const {selectHealthFundHandler} = this.context;
        selectHealthFundHandler(e);
    }

    render() {
        const practice = toJS(this.context.practice);
        const {
            isHospitalPage,
            rootStore: {
                paramStore: {isHfWidget, isClient},
                healthFundStore: {healthFund},
            },
        } = this.context;

        const {
            description,
            files,
            images,
            outOfNetworkInformation,
            tagline,
            tagline2,
            videos,
        } = practice;
        return (
            <>
                {isHospitalPage &&
                    outOfNetworkInformation &&
                    !!healthFund?.id && (
                        <div
                            className="notification error"
                            dangerouslySetInnerHTML={{
                                __html: outOfNetworkInformation,
                            }}
                        ></div>
                    )}
                {(tagline || tagline2) && (
                    <p className="notification neutral">
                        <strong>{tagline}</strong>
                        {isHospitalPage && tagline2 && <br />}
                        {tagline2}
                    </p>
                )}
                {(practice.offersHicaps || !!practice.extraInfo) && (
                    <>
                        {this.renderHicaps()}
                        <ExtraInfo
                            comment={practice.extraInfo?.comment}
                            disabledAccess={practice.disabledAccess}
                            onsiteParking={practice.onsiteParking}
                            openHours={practice.extraInfo?.openHours}
                            openIndicator={practice.openIndicator}
                            paymentOptions={practice.extraInfo?.paymentOptions}
                            serviceOptions={practice.extraInfo?.serviceOptions}
                        />
                    </>
                )}
                {description && (
                    <article className="description">
                        <CollapsibleText
                            collapsed={true}
                            maxWords={35}
                            renderHtml={true}
                            text={description}
                        />
                    </article>
                )}
                {files?.length > 0 && (
                    <section className="related">
                        <h2>Documents</h2>
                        <ul>
                            {files.map((file) =>
                                this.renderPracticeFile(file),
                            )}
                        </ul>
                    </section>
                )}
                {!this.context.rootStore.paramStore.isClient &&
                    images?.length > 0 && (
                        <section>
                            <h2>Images</h2>
                            <ImageGallery
                                images={images}
                                onClick={this.onClickImage}
                            />
                        </section>
                    )}
                {!this.context.rootStore.paramStore.isClient &&
                    videos?.length > 0 && (
                        <section>
                            <h2>Relevant Videos</h2>
                            {videos.map((video) =>
                                video.link ? (
                                    <Video
                                        key={video.key}
                                        preventTracking={true}
                                        profileId={practice.id}
                                        video={video}
                                    />
                                ) : null,
                            )}
                        </section>
                    )}
                <HealthFundPicker
                    handleSelectHealthFund={(e) => this.HFselector(e)}
                    initialOptionText={"I don't have private health insurance"}
                    isHidden={isHfWidget || (isClient && !isHospitalPage)}
                    stopAutoScroll={true}
                />
                {this.renderPractitionersList()}
            </>
        );
    }
}
